"use client";
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BASE_URL, UPLOAD_URL } from "../../config/config";
import { UserRound } from "lucide-react";

function ReviewList({ activeFilter }) {
  const { id: productId } = useParams();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);

  const visibleCount = 3; // how many reviews visible per slide
  const maxIndex = Math.max(0, Math.ceil(reviews.length / visibleCount) - 1);

  useEffect(() => {
    async function fetchReviews() {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/rating/${productId}`);
        const data = await response.json();

        const normalizedReviews = Array.isArray(data.data)
          ? data.data
          : [data.data];
        const mappedReviews = normalizedReviews.map((review) => ({
          id: review.id,
          rating: parseInt(review.stars),
          title: review.review,
          date: review.create_audit_time,
          author: review.user_name,
          avatar: `${UPLOAD_URL}/users/${review.image}`,
          hasMedia: !!review.image,
          hasDescription: !!review.review,
        }));

        setReviews(mappedReviews);
      } catch (err) {
        console.error(err);
        setError("Failed to load reviews.");
      } finally {
        setLoading(false);
      }
    }

    fetchReviews();
  }, [productId]);

  const filteredReviews = reviews.filter((review) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "media") return review.hasMedia;
    if (activeFilter === "description") return review.hasDescription;
    return true;
  });

  const prevSlide = () => setCurrentIndex((prev) => Math.max(prev - 1, 0));
  const nextSlide = () => setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  const getTransformValue = () => `translateX(-${currentIndex * 100}%)`;

  if (loading) return <div className="text-center py-8">Loading reviews...</div>;
  if (error) return <div className="text-center text-red-600 py-8">{error}</div>;

  return (
    <section className="w-full mt-10">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-neutral-800">Customer Reviews</h2>

        {/* Navigation Buttons */}
        {filteredReviews.length > visibleCount && (
          <div className="flex gap-2">
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className="p-2 rounded-full border border-yellow-500 text-yellow-600 hover:bg-yellow-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={nextSlide}
              disabled={currentIndex >= maxIndex}
              className="p-2 rounded-full border border-yellow-500 text-yellow-600 hover:bg-yellow-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Carousel Wrapper */}
      <div className="relative w-full overflow-hidden">
        <div
          ref={containerRef}
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: getTransformValue() }}
        >
          {filteredReviews.length > 0 ? (
            filteredReviews.map((review) => (
              <div
                key={review.id}
                className="flex-none px-3 w-full sm:w-1/2 lg:w-1/3"
              >
                <div className="bg-white border rounded-lg shadow-sm p-6 h-full flex flex-col justify-between">
                  {/* Stars */}
                  <div
                    className="flex gap-1 mb-3"
                    role="img"
                    aria-label={`${review.rating} out of 5 stars`}
                  >
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className={`w-5 h-5 ${
                          i < review.rating
                            ? "text-yellow-400"
                            : "text-gray-300"
                        }`}
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>

                  {/* Title */}
                  <h4 className="text-lg font-semibold mb-2 line-clamp-2">
                    {review.title || "No review text"}
                  </h4>

                  {/* Date */}
                  <time className="text-sm text-gray-500 mb-4">
                    {new Date(review.date).toLocaleDateString()}
                  </time>

                  {/* Author */}
                <div className="flex items-center gap-3 mt-auto">
                    {review.avatar &&
                    review.avatar !== `${UPLOAD_URL}/users/undefined` &&
                    review.avatar !== `${UPLOAD_URL}/users/null` &&
                    !review.avatar.endsWith("/users/") ? (
                      <img
                        src={review.avatar}
                        alt={review.author || "User"}
                        onError={(e) => (e.currentTarget.style.display = "none")}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <UserRound className="text-gray-500 w-6 h-6" />
                      </div>
                    )}
                    <span className="font-medium text-gray-700">{review.author}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center w-full py-12 text-neutral-500">
              No reviews found for the selected filter.
            </p>
          )}
        </div>
      </div>

      {/* Dots */}
      {maxIndex > 0 && (
        <div className="flex gap-2 justify-center mt-6">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                currentIndex === index ? "bg-yellow-500" : "bg-gray-300"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default ReviewList;
